<template>
  <div class="xo-page" v-loading="loading">

    <!-- Header — title row (identity + primary action) sits above a
         dedicated filter bar. Chrome comes from styles/list-page.scss so
         every list page in the app shares one look. -->
    <div class="xo-page-header">
      <div class="xo-inner">

        <div class="xo-titlebar">
          <div>
            <h1 class="xo-title">Order History</h1>
            <p class="xo-subtitle">{{ resultSummary }}</p>
          </div>
          <button
            class="xo-btn xo-btn--navy"
            :disabled="csvDownloading"
            @click="downloadCSV"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/><line x1="10" y1="9" x2="14" y2="9"/></svg>
            {{ csvDownloading ? 'Exporting…' : 'Export CSV' }}
          </button>
        </div>

        <div class="xo-toolbar">
          <!-- Client-side substring search — filters only the currently-loaded
               page window (see filteredData). No server round-trip so results
               are instant. Matches order_number, customer/user name, and the
               pickup/dropoff address strings. Given the most width because it
               is the control customers reach for first. -->
          <div class="xo-field xo-field--search">
            <svg class="xo-field-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              v-model="searchTerm"
              type="search"
              class="xo-search"
              placeholder="Search order no., user, pickup or drop-off"
              aria-label="Search orders"
            />
          </div>

          <!-- Date range filter — passes start_date / end_date to the
               orders backend so the range is honoured server-side and
               the response isn't silently trimmed to the default cap.
               Bound to a single "range" ref (Element Plus daterange). -->
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            value-format="YYYY-MM-DD"
            format="DD MMM YYYY"
            range-separator="to"
            start-placeholder="From"
            end-placeholder="To"
            :clearable="true"
            size="default"
            class="oh-date-range"
            @change="onDateRangeChange"
          />

          <!-- Searchable user-name dropdown — options are the unique user_name
               values from the currently-loaded set (no getusers call, works for
               every role). Purely client-side; selecting a name filters the
               visible rows and does not trigger a fetch. -->
          <el-select
            v-model="userNameFilter"
            placeholder="All users"
            clearable
            filterable
            size="default"
            class="oh-user-select"
          >
            <el-option
              v-for="name in orderUserNames"
              :key="name"
              :label="name"
              :value="name"
            />
          </el-select>

          <!-- Include cancelled — flips the &include_cancelled=1 query param and
               refetches. Cancelled rows are excluded server-side by default. -->
          <label class="xo-chip" :class="{ 'xo-chip--on': includeCancelled }">
            <input
              type="checkbox"
              v-model="includeCancelled"
              @change="onIncludeCancelledChange"
            />
            <span>Include cancelled</span>
          </label>

          <!-- Only offered once something is actually filtered, so the bar
               stays quiet in its default state. -->
          <button v-if="hasActiveFilters" class="xo-reset" @click="resetAllFilters">
            Reset
          </button>
        </div>

      </div>
    </div>

    <div class="xo-inner oh-list-wrap">

      <!-- Table -->
      <div class="oh-table">

        <!-- Column headers -->
        <div class="oh-thead">
          <div class="oh-th oh-th--order">ORDER</div>
          <div class="oh-th oh-th--status">STATUS</div>
          <div class="oh-th oh-th--user">USER</div>
          <div class="oh-th oh-th--date">DATE</div>
          <div class="oh-th oh-th--amount">AMOUNT</div>
          <div class="oh-th oh-th--from">FROM</div>
          <div class="oh-th oh-th--to">TO</div>
          <div class="oh-th oh-th--action"></div>
        </div>

        <!-- Rows -->
        <div class="oh-tbody">
          <div
            v-for="(row, i) in paginatedData"
            :key="row.id"
            :id="`oh-row-${row.id}`"
            class="oh-row-wrap"
            :class="{ 'oh-row-wrap--open': expandedRows.has(row.id) }"
          >
            <div class="oh-row" @click="toggleRow(row)">
              <div class="oh-td oh-td--order">
                <span class="oh-ref">{{ row.order_number }}</span>
              </div>
              <div class="oh-td oh-td--status">
                <!-- Composed label from utils/orderStatus.ts. Reflects
                     order_status + verification_status together — a
                     driver-dropped order shows "Pending approval" until
                     Xobo Ops verifies the delivery documents, then flips
                     to "Delivered". Previously said "Delivered" the moment
                     the driver marked drop, which was misleading given
                     the customer hasn't been charged (hold-until-approve)
                     and the driver hasn't been credited either. -->
                <span
                  class="oh-badge"
                  :class="getStatusClass(row)"
                  :title="statusFor(row).hint"
                >
                  {{ statusLabel(row) }}
                </span>
              </div>
              <div class="oh-td oh-td--user">
                <span class="oh-td-text">{{ row.user_name }}</span>
              </div>
              <div class="oh-td oh-td--date">
                <span class="oh-td-muted">{{ transformDateSQL(row.scheduled_time) }}</span>
              </div>
              <div class="oh-td oh-td--amount">
                <span class="oh-td-amount">KES {{ row.cost.toLocaleString() }}</span>
              </div>
              <div class="oh-td oh-td--from">
                <span class="oh-td-muted">{{ row.pickup }}</span>
              </div>
              <div class="oh-td oh-td--to">
                <span class="oh-td-muted">{{ row.dropoff }}</span>
              </div>
              <div class="oh-td oh-td--action">
                <span class="oh-view-link">
                  {{ expandedRows.has(row.id) ? 'Hide' : 'View' }}
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </span>
              </div>
            </div>

            <!-- Expanded detail -->
            <Transition name="expand">
              <div class="oh-detail" v-if="expandedRows.has(row.id)" v-loading="row.orderDetails == undefined">
                <!-- Doc-verification rejection banner — mounted above the
                     detail grid so the reason is the first thing the user
                     sees when opening a flagged order. Label is looked up
                     from the onboarding rejection_reasons_config; falls back
                     to the raw code if the lookup misses. -->
                <div
                  v-if="row.orderDetails != undefined && rejectionInfo(row) != null"
                  class="oh-reject-banner"
                >
                  <div class="oh-reject-banner__head">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span class="oh-reject-banner__title">Delivery documents rejected</span>
                    <span class="oh-reject-banner__label">{{ rejectionInfo(row)!.label }}</span>
                  </div>
                  <p v-if="rejectionInfo(row)!.note" class="oh-reject-banner__note">
                    {{ rejectionInfo(row)!.note }}
                  </p>
                </div>

                <!-- Ops-closed banner (verification_status=5). Visually
                     distinct from rejection: this state means the order
                     was permanently closed and the customer was never
                     charged; the reason fields carry the CLOSING reason,
                     not a doc-rejection reason. Was previously conflated
                     with rejected via a single banner + shared helper —
                     see closedInfo() vs rejectionInfo() in <script>. -->
                <div
                  v-if="row.orderDetails != undefined && closedInfo(row) != null"
                  class="oh-close-banner"
                >
                  <div class="oh-close-banner__head">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/></svg>
                    <span class="oh-close-banner__title">Order closed by Xobo Ops</span>
                    <span class="oh-close-banner__label">{{ closedInfo(row)!.label }}</span>
                  </div>
                  <p v-if="closedInfo(row)!.note" class="oh-close-banner__note">
                    {{ closedInfo(row)!.note }}
                  </p>
                  <p class="oh-close-banner__hint">
                    You have not been charged for this order and will not be.
                  </p>
                </div>
                <div class="oh-detail-grid" v-if="row.orderDetails != undefined">

                  <!-- Order info card — list row fields first (Mission Control pattern) -->
                  <div class="oh-dcard">
                    <div class="oh-dcard-head">
                      <div class="oh-dcard-icon oh-dcard-icon--blue">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/></svg>
                      </div>
                      <div class="oh-dcard-label">Order Info</div>
                    </div>
                    <div class="oh-dkv-list">
                      <div class="oh-dkv"><span class="oh-dk">Amount</span><span class="oh-dv oh-dv--navy">KES {{ row.cost.toLocaleString() }}</span></div>
                      <div class="oh-dkv"><span class="oh-dk">Distance</span><span class="oh-dv">{{ row.orderDetails?.order_details?.distance_value ?? row.distance_value }} km</span></div>
                      <div class="oh-dkv"><span class="oh-dk">Vehicle Type</span><span class="oh-dv">{{ vehicleTypeLabel(row) }}</span></div>
                      <div v-for="x in extraInfoRows(row)" :key="x.label" class="oh-dkv">
                        <span class="oh-dk">{{ x.label }}</span>
                        <span class="oh-dv oh-dv--wrap">{{ x.value }}</span>
                      </div>
                      <div class="oh-dkv"><span class="oh-dk">Requested by</span><span class="oh-dv">{{ row.orderDetails?.customer_details?.user_name ?? row.user_name }}</span></div>
                      <div class="oh-dkv"><span class="oh-dk">User Email</span><span class="oh-dv">{{ row.orderDetails.customer_details.user_email || '—' }}</span></div>
                      <div class="oh-dkv"><span class="oh-dk">User Phone No.</span><span class="oh-dv">{{ row.orderDetails.customer_details.phone_number || '—' }}</span></div>
                      <div class="oh-dkv" v-if="row.orderDetails.order_details.notes?.trim()">
                        <span class="oh-dk">Notes</span>
                        <span class="oh-dv oh-dv--wrap">{{ row.orderDetails.order_details.notes }}</span>
                      </div>
                      <div class="oh-dkv" v-if="row.orderDetails.order_details.manifest_number?.trim()">
                        <span class="oh-dk">Manifest</span>
                        <span class="oh-dv">{{ row.orderDetails.order_details.manifest_number }}</span>
                      </div>
                      <div class="oh-dkv" v-if="row.orderDetails.delivery_logs.find((log:any) => log.log_type === 'Picked')">
                        <span class="oh-dk">Picked up</span>
                        <span class="oh-dv">{{ transformDate(row.orderDetails.delivery_logs.find((log:any) => log.log_type === 'Picked')['log_time']) }}</span>
                      </div>
                      <div class="oh-dkv" v-if="row.orderDetails.delivery_logs.find((log:any) => log.log_type === 'Dropped')">
                        <span class="oh-dk">Delivered</span>
                        <span class="oh-dv oh-dv--green">{{ transformDate(row.orderDetails.delivery_logs.find((log:any) => log.log_type === 'Dropped')['log_time']) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Route card -->
                  <div class="oh-dcard">
                    <div class="oh-dcard-head">
                      <div class="oh-dcard-icon oh-dcard-icon--purple">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 6-9 13-9 13S3 16 3 10a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      </div>
                      <div class="oh-dcard-label">Route</div>
                    </div>
                    <div class="oh-route">
                      <!-- Pickup -->
                      <div class="oh-stop">
                        <div class="oh-stop-track">
                          <div class="oh-stop-dot oh-stop-dot--green"></div>
                          <div class="oh-stop-line"></div>
                        </div>
                        <div class="oh-stop-text">
                          <div class="oh-stop-type">Pick-up</div>
                          <div class="oh-stop-name">{{ row.orderDetails.paths[0]?.name || row.orderDetails.order_details.from_name }}</div>
                          <template v-if="stopDetailOf(row, 0)">
                            <div class="oh-stop-contact">
                              <span v-if="stopDetailOf(row, 0)!.contact_name">{{ stopDetailOf(row, 0)!.contact_name }}</span>
                              <span v-if="stopDetailOf(row, 0)!.contact_phone"><a :href="telHref(stopDetailOf(row, 0)!.contact_phone)" class="oh-stop-tel" @click.stop>{{ formatPhone(stopDetailOf(row, 0)!.contact_phone) }}</a></span>
                              <span v-if="stopDetailOf(row, 0)!.reference">Ref {{ stopDetailOf(row, 0)!.reference }}</span>
                            </div>
                            <div v-if="stopDetailOf(row, 0)!.instructions" class="oh-stop-instructions">{{ stopDetailOf(row, 0)!.instructions }}</div>
                          </template>
                        </div>
                      </div>
                      <!-- Each dropoff stop -->
                      <div
                        v-for="(stop, si) in (row.orderDetails.paths as any[]).slice(1)"
                        :key="si"
                        class="oh-stop"
                      >
                        <div class="oh-stop-track">
                          <div class="oh-stop-dot" :class="si === row.orderDetails.paths.length - 2 ? 'oh-stop-dot--amber' : 'oh-stop-dot--mid'"></div>
                          <div class="oh-stop-line" v-if="si < row.orderDetails.paths.length - 2"></div>
                        </div>
                        <div class="oh-stop-text">
                          <div class="oh-stop-type oh-stop-type--drop">
                            <span class="oh-stop-num">{{ si + 1 }}</span>
                            Drop-off
                          </div>
                          <div class="oh-stop-name">{{ stop.name }}</div>
                          <template v-if="stopDetailOf(row, si + 1)">
                            <div class="oh-stop-contact">
                              <span v-if="stopDetailOf(row, si + 1)!.contact_name">{{ stopDetailOf(row, si + 1)!.contact_name }}</span>
                              <span v-if="stopDetailOf(row, si + 1)!.contact_phone"><a :href="telHref(stopDetailOf(row, si + 1)!.contact_phone)" class="oh-stop-tel" @click.stop>{{ formatPhone(stopDetailOf(row, si + 1)!.contact_phone) }}</a></span>
                              <span v-if="stopDetailOf(row, si + 1)!.reference">Ref {{ stopDetailOf(row, si + 1)!.reference }}</span>
                            </div>
                            <div v-if="stopDetailOf(row, si + 1)!.instructions" class="oh-stop-instructions">{{ stopDetailOf(row, si + 1)!.instructions }}</div>
                          </template>
                        </div>
                      </div>
                    </div>
                    <div class="oh-detail-actions">
                      <a v-if="row.order_status < 4" :href="returnOrderTrackingLink(row)" class="oh-track-link">Track this order →</a>
                    </div>

                    <!-- Inline delivery documents gallery -->
                    <div v-if="hasDeliveryDocuments(row.orderDetails.paths)" class="oh-docs-section">
                      <button
                        class="oh-docs-toggle"
                        @click.stop="toggleDocs(row.id)"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                        Delivery Documents
                        <span class="oh-docs-count">{{ extractDocumentsInfo(row.orderDetails.paths).length }}</span>
                        <svg class="oh-docs-chevron" :class="{ 'oh-docs-chevron--open': openDocs.has(row.id) }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                      </button>

                      <Transition name="docs-expand">
                        <div v-if="openDocs.has(row.id)" class="oh-docs-gallery">
                          <div
                            v-for="(doc, di) in extractDocumentsInfo(row.orderDetails.paths)"
                            :key="di"
                            class="oh-doc-thumb"
                            @click.stop="openDoc(doc)"
                          >
                            <div class="oh-doc-img-wrap">
                              <img v-if="isImage(doc.src)" :src="doc.src" :alt="doc.title" class="oh-doc-img" @error="(e: any) => e.target.style.display='none'" />
                              <div v-else class="oh-doc-file-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#475569" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                <span class="oh-doc-ext">{{ getExt(doc.src) }}</span>
                              </div>
                              <div class="oh-doc-overlay">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                              </div>
                            </div>
                            <div class="oh-doc-label">{{ doc.title || 'Document ' + (di + 1) }}</div>
                          </div>
                        </div>
                      </Transition>
                    </div>
                  </div>

                  <!-- Driver card -->
                  <div class="oh-dcard oh-dcard--driver">
                    <div class="oh-dcard-head">
                      <div class="oh-dcard-icon oh-dcard-icon--teal">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9488" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                      </div>
                      <div class="oh-dcard-label">Driver</div>
                    </div>
                    <div class="oh-driver">
                      <div class="oh-driver-avatar">{{ row.orderDetails.driver_details.name?.charAt(0)?.toUpperCase() }}</div>
                      <div>
                        <div class="oh-driver-name">{{ row.orderDetails.driver_details.name }}</div>
                        <div class="oh-driver-plate">{{ row.orderDetails.driver_details.number_plate }}</div>
                      </div>
                    </div>

                    <!-- Driver rating. Same rules as orders-service POST
                         /orders/rate: only the person who booked the order,
                         only once Xobo approved the delivery, and only for 7
                         days from the first rating. When those hold, the form;
                         otherwise the saved rating read-only (colleagues, or
                         after the window), or nothing. Stars, chips and the
                         comment only edit a local draft; nothing is saved
                         until Submit. Hover previews the stars; leaving the
                         group falls back to the draft. No .stop needed: the
                         detail is a sibling of .oh-row, not inside it. -->
                    <div v-if="canEditRating(row)" class="oh-rating">
                      <div :id="`oh-rate-label-${row.id}`" class="oh-stop-type">Rate your driver</div>
                      <!-- A radio group: one star is the checked value,
                           arrow keys move it, and only the checked star
                           (or the first) is in the tab order. -->
                      <div
                        class="oh-stars"
                        role="radiogroup"
                        :aria-labelledby="`oh-rate-label-${row.id}`"
                        @pointerleave="ratingHover.delete(row.id)"
                      >
                        <button
                          v-for="n in 5"
                          :key="n"
                          type="button"
                          role="radio"
                          class="oh-star"
                          :class="{ 'oh-star--on': n <= ratingShown(row) }"
                          :aria-checked="draftOf(row).rating === n"
                          :aria-label="`${n} star${n > 1 ? 's' : ''}`"
                          :tabindex="n === (draftOf(row).rating ?? 1) ? 0 : -1"
                          :disabled="ratingSaving.has(row.id)"
                          @pointerenter="previewRating(row, n, $event)"
                          @click="pickRating(row, n)"
                          @keydown="onStarKey(row, n, $event)"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                        </button>
                      </div>
                      <div class="oh-rating-caption" aria-live="polite">{{ ratingCaption(row) }}</div>
                      <div v-if="ratingDeadline(row)" class="oh-rating-caption">
                        You can change it until {{ ratingDeadline(row) }}
                      </div>
                      <div v-if="draftOf(row).rating" class="oh-chips">
                        <button
                          v-for="s in RATING_SUGGESTIONS[draftOf(row).rating!]"
                          :key="s"
                          type="button"
                          class="oh-chip"
                          :class="{ 'oh-chip--on': draftOf(row).comment === s }"
                          :aria-pressed="draftOf(row).comment === s"
                          :disabled="ratingSaving.has(row.id)"
                          @click="pickSuggestion(row, s)"
                        >
                          {{ s }}
                        </button>
                      </div>
                      <textarea
                        v-model="draftOf(row).comment"
                        class="oh-rating-comment"
                        rows="2"
                        maxlength="500"
                        placeholder="Add a comment (optional)"
                        aria-label="Comment for your driver"
                        :disabled="ratingSaving.has(row.id)"
                      ></textarea>
                      <button
                        type="button"
                        class="xo-btn xo-btn--navy oh-rating-submit"
                        :disabled="!canSubmitRating(row)"
                        @click="submitRating(row)"
                      >
                        {{ ratingSaving.has(row.id) ? "Saving…" : "Submit" }}
                      </button>
                    </div>

                    <!-- Read-only: a rating exists but this viewer cannot
                         change it (a colleague of the booker, the 7 days
                         are over, or the server refused a change). -->
                    <div v-else-if="showSavedRating(row)" class="oh-rating oh-rating--readonly">
                      <div class="oh-stop-type">Driver rating</div>
                      <div
                        v-if="savedRating(row)"
                        class="oh-stars oh-stars--static"
                        role="img"
                        :aria-label="`Rated ${savedRating(row)} out of 5`"
                      >
                        <svg
                          v-for="n in 5"
                          :key="n"
                          class="oh-star-static"
                          :class="{ 'oh-star--on': n <= savedRating(row)! }"
                          width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
                        ><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      </div>
                      <p v-if="row.driver_rating_comment" class="oh-rating-saved-comment">{{ row.driver_rating_comment }}</p>
                      <div class="oh-rating-caption">{{ readOnlyRatingCaption(row) }}</div>
                    </div>
                  </div>

                </div>
              </div>
            </Transition>
          </div>
        </div>

      </div>

      <!-- Pagination — shown only when there's more than one page -->
      <div class="xo-pager" v-if="!loading && totalPages > 1">
        <div class="xo-pager-info">
          Showing <strong>{{ rangeStart }}–{{ rangeEnd }}</strong> of <strong>{{ filteredData.length }}</strong>
        </div>
        <div class="xo-pager-controls">
          <button
            class="xo-pager-btn"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
            aria-label="Previous page"
          >
            ‹
          </button>
          <template v-for="(p, idx) in pageButtons" :key="idx">
            <span v-if="p === '…'" class="xo-pager-ellipsis">…</span>
            <button
              v-else
              class="xo-pager-btn"
              :class="{ 'xo-pager-btn--active': p === currentPage }"
              @click="goToPage(p as number)"
            >
              {{ p }}
            </button>
          </template>
          <button
            class="xo-pager-btn"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>

      <!-- Empty state — distinguish "no orders at all" from "filters
           narrowed everything out" so the CTA doesn't appear on an
           active-filter miss.
           Previously the "filters narrowed" branch only fired when
           tableData.data.length > 0, i.e. only when the CLIENT-side
           filters (search / user-name) emptied a non-empty server
           response. Date-range + include-cancelled are SERVER-side
           filters — they re-fetch and shrink tableData.data to []
           on a miss, which fell through to the 📦 "Place your first
           order" branch and confused customers with hundreds of
           orders sitting just outside the window. Now the check
           uses hasActiveFilters directly (which includes date-range
           + include-cancelled) so the Clear-filters CTA appears
           whenever any active filter is why the list is empty. -->
      <div class="oh-empty" v-if="!loading && filteredData.length === 0 && hasActiveFilters">
        <div class="oh-empty-icon">🔍</div>
        <div class="oh-empty-text">
          {{ searchTerm.trim() ? `No orders match “${searchTerm}”` : 'No orders match your filters' }}
        </div>
        <button class="xo-btn xo-btn--ghost" @click="resetAllFilters">Clear filters</button>
      </div>
      <div class="oh-empty" v-else-if="!loading && filteredData.length === 0">
        <div class="oh-empty-icon">📦</div>
        <div class="oh-empty-text">No orders yet</div>
        <button class="xo-btn xo-btn--navy" @click="router.push('/request')">Place your first order</button>
      </div>

    </div>

    <!-- Document preview modal — replaces the old bespoke gallery+iframe
         modal with the shared DocumentPreview component so image / pdf /
         office / text / unknown all render correctly, not just image vs
         "iframe and hope". Each thumbnail click opens the doc that was
         clicked; the sidebar/thumbstrip navigation is dropped in favour
         of returning to the thumb gallery beneath the row. -->
    <DocumentPreview :doc="modalDoc" @close="closeDoc" />

  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed, watch, nextTick } from "vue";
import router from "../../router";
import axios from "axios";
import { ElMessage } from "element-plus";
import { useAuthStore } from "../../store/authStore";
import { DateTime } from "luxon";
import { unparse } from "papaparse";
import DocumentPreview from "../DocumentPreview.vue";
import { loadRejectionReasons, labelForCode, type RejectionReason } from "../../utils/rejectionReasons";
import { orderStatusView, orderHistoryBadgeClass } from "../../utils/orderStatus";
import {
  type StopDetailView,
  stopDetailsByIndex,
  hasVisibleStopDetail,
  formatPhone,
  telHref,
  orderTypeLabel,
  pickupLabel,
  loadersLabel,
  pickupModeLabel,
  dropReferences,
} from "../../utils/orderExtraInfo";
import { csvSafeRow } from "../../utils/csvSafe";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

const loading = ref(false);
const tableKey = ref("id");
const tableData = reactive({ data: <any>[] });
const expandedRows = reactive(new Set<any>());

// ─── Toolbar filters ────────────────────────────────────────────
// Toggles the &include_cancelled=1 query param on the orders fetch.
// Kept as a top-level ref so onIncludeCancelledChange stays a thin
// wrapper that just re-invokes postData() (same pattern as the date
// range).
const includeCancelled = ref(false);
// Client-side substring search. Runs only over rows already loaded
// (the currently-fetched window — see filteredData below). Widen the
// date range or toggle "Include cancelled" to pull more rows into the
// searchable set.
const searchTerm = ref("");
// User-name dropdown filter. Options come from unique user_name values
// in the loaded set (no getusers call — works for all roles). Exact
// match; the dropdown itself is `filterable` so the user can type-ahead
// to find a name in a long list.
const userNameFilter = ref("");

// Options for the searchable user-name dropdown — unique, sorted,
// case-insensitive.
const orderUserNames = computed((): string[] => {
  const names: string[] = tableData.data
    .map((row: any) => String(row?.user_name ?? "").trim())
    .filter((n: string) => n.length > 0);
  return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
});

// Client-side pagination — 10 rows per page. CSV export still grabs the full set.
const PAGE_SIZE = 10;
const currentPage = ref(1);

// Filter the currently-loaded rows by the free-text search AND by the
// user-name dropdown. Window-only — matches restricted to rows already
// returned by the last fetch, NOT the full historical order set.
// Search is case-insensitive substring against order_number,
// customer/user name, and the pickup/dropoff address strings.
// User-name filter is an exact match against row.user_name.
const filteredData = computed(() => {
  const q = searchTerm.value.trim().toLowerCase();
  const u = userNameFilter.value.trim();
  if (!q && !u) return tableData.data;
  return tableData.data.filter((row: any) => {
    const okUser = !u || String(row?.user_name ?? "").trim() === u;
    if (!okUser) return false;
    if (!q) return true;
    const hay = [
      row?.order_number,
      row?.customer_name,
      row?.user_name,
      row?.pickup,
      row?.dropoff,
    ]
      .filter((v) => v != null)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
});

const clearClientFilters = () => {
  searchTerm.value = "";
  userNameFilter.value = "";
  currentPage.value = 1;
};

// Any filter at all — client-side (search / user) or server-side (date
// range / include cancelled). Drives the Reset button's visibility and
// the subtitle's "of N" suffix.
const hasActiveFilters = computed(() =>
  Boolean(searchTerm.value.trim() || userNameFilter.value || dateRange.value || includeCancelled.value),
);

// Clears everything, then refetches only because the date range and the
// include-cancelled toggle are server-side params.
const resetAllFilters = () => {
  const needsRefetch = Boolean(dateRange.value || includeCancelled.value);
  clearClientFilters();
  dateRange.value = null;
  includeCancelled.value = false;
  if (needsRefetch) postData();
};

// Header subtitle — gives the count its own home so the toolbar doesn't
// have to carry it, and makes it obvious when filters are hiding rows.
const resultSummary = computed(() => {
  if (loading.value) return "Loading orders…";
  const shown = filteredData.value.length;
  const total = tableData.data.length;
  const noun = shown === 1 ? "order" : "orders";
  if (hasActiveFilters.value && shown !== total) return `${shown} ${noun} of ${total}`;
  return `${shown} ${noun}`;
});

const totalPages = computed(() => Math.max(1, Math.ceil(filteredData.value.length / PAGE_SIZE)));
const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE;
  return filteredData.value.slice(start, start + PAGE_SIZE);
});
// If data shrinks (or never grows past 1 page) keep currentPage valid.
watch(totalPages, (n) => { if (currentPage.value > n) currentPage.value = n; });
// Reset to page 1 when either client filter changes so the user isn't
// stranded on a now-empty page after narrowing results.
watch([searchTerm, userNameFilter], () => { currentPage.value = 1; });

const goToPage = (n: number) => {
  if (n < 1 || n > totalPages.value) return;
  currentPage.value = n;
  // Scroll the table back to the top so the user sees row 1 of the new page.
  if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
};

// Build a compact list of page buttons with ellipses for long page counts.
// e.g. for current=7 of 20: [1, '…', 6, 7, 8, '…', 20]
const pageButtons = computed<(number | "…")[]>(() => {
  const total = totalPages.value;
  const cur = currentPage.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | "…")[] = [1];
  const left = Math.max(2, cur - 1);
  const right = Math.min(total - 1, cur + 1);
  if (left > 2) out.push("…");
  for (let i = left; i <= right; i++) out.push(i);
  if (right < total - 1) out.push("…");
  out.push(total);
  return out;
});

const rangeStart = computed(() =>
  filteredData.value.length === 0 ? 0 : (currentPage.value - 1) * PAGE_SIZE + 1,
);
const rangeEnd = computed(() =>
  Math.min(currentPage.value * PAGE_SIZE, filteredData.value.length),
);
const openDocs = reactive(new Set<any>());
const toggleDocs = (id: any) => {
  if (openDocs.has(id)) openDocs.delete(id);
  else openDocs.add(id);
};

// Expands a row and resolves once its detail (and so the rating section)
// can render. Shared by a click and the receipt-email deep link.
const openRow = (row: any): Promise<void> => {
  expandedRows.add(row.id);
  // Seed the rating draft here, not from the template, so render never
  // mutates state. The rating section only exists inside an expanded row.
  if (!ratingDrafts.has(row.id)) {
    ratingDrafts.set(row.id, { rating: savedRating(row), comment: row.driver_rating_comment ?? "" });
  }
  return row.orderDetails ? Promise.resolve() : loadOrderDetails(row);
};

const toggleRow = (row: any) => {
  if (expandedRows.has(row.id)) expandedRows.delete(row.id);
  else openRow(row);
};

// Single source for fetching one order's full detail (used by the expand
// view and the CSV export). Caches the payload back onto the row so repeat
// expands/exports are instant.
const fetchOrderDetails = async (row: any) => {
  if (row.orderDetails) return row.orderDetails;
  // The JWT goes in the header, never the URL: this response carries receiver contacts.
  const r = await axios.get(
    `${import.meta.env.VITE_ORDERS_SERVICE}orders?order_number=${encodeURIComponent(row.order_number)}`,
    { headers: store.getToken ? { Authorization: `Bearer ${store.getToken}` } : {} },
  );
  if (r.status == 200 && r.data.status) {
    const idx = tableData.data.findIndex((el: any) => el.id == row.id);
    if (idx > -1) tableData.data[idx].orderDetails = r.data.data;
    return r.data.data;
  }
  return row.orderDetails;
};

const loadOrderDetails = async (row: any) => {
  try {
    await fetchOrderDetails(row);
  } catch { ElMessage({ message: "Error loading order details", type: "error" }); }
};

// Vehicle type follows Mission Control: list API joins vehicle_type and
// returns vehicle_name; Redis driver_details is only a fallback.
const resolveVehicleTypeName = (row: any) =>
  row.vehicle_name?.trim()
  || row.vehicle_type_name?.trim()
  || row.orderDetails?.driver_details?.vehicle_type_name?.trim()
  || '';

const vehicleTypeLabel = (row: any) => resolveVehicleTypeName(row) || '—';

// ─── Extra order info ───────────────────────────────────────────
// Type, pickup, goods and loaders: on the company list row, with the
// order detail as a fallback. Rows only show when the order has a value,
// so orders placed before these fields look as they did.
const extraOf = (row: any, key: string) => row?.[key] ?? row?.orderDetails?.order_details?.[key];

const extraInfoRows = (row: any): { label: string; value: string }[] => {
  const out: { label: string; value: string }[] = [];
  const type = orderTypeLabel(extraOf(row, "order_type"));
  if (type) out.push({ label: "Type", value: type });
  const pickup = pickupLabel(extraOf(row, "pickup_mode"), extraOf(row, "scheduled_time"));
  if (pickup) out.push({ label: "Pickup", value: pickup });
  const goods = String(extraOf(row, "goods_description") ?? "").trim();
  if (goods) out.push({ label: "Goods", value: goods });
  const loaders = loadersLabel(extraOf(row, "loaders_count"));
  if (loaders) out.push({ label: "Loaders", value: loaders });
  return out;
};

// Receiver contacts per stop: only on the signed-in order detail
// (stop_details), keyed by stop index (0 = pickup). Cached per payload.
const stopDetailMaps = new WeakMap<object, Map<number, StopDetailView>>();
const stopDetailOf = (row: any, index: number): StopDetailView | null => {
  const od = row?.orderDetails;
  if (!od || typeof od !== "object") return null;
  let map = stopDetailMaps.get(od);
  if (!map) {
    map = stopDetailsByIndex(od.stop_details);
    stopDetailMaps.set(od, map);
  }
  const d = map.get(index);
  return hasVisibleStopDetail(d) ? d! : null;
};

// ─── Driver rating ──────────────────────────────────────────────
// Hover and in-flight state are keyed by row id (same pattern as
// expandedRows / openDocs) so previewing stars on one row never lights
// up another. The saved values live on the row itself (driver_rating,
// driver_rating_comment, driver_rated_at, rating_editable_until, is_mine,
// can_rate, all sent by the orders list); edits go to a draft until Submit.
const ratingHover = reactive(new Map<any, number>());
const ratingSaving = reactive(new Set<any>());
const ratingDrafts = reactive(new Map<any, { rating: number | null; comment: string }>());
const savedRating = (row: any): number | null => Number(row.driver_rating) || null;

// Who may rate, mirroring orders-service (POST /orders/rate, and can_rate
// on each list row). The server enforces all of it; these only decide
// what to show:
//   - only the person who booked the order (is_mine); colleagues in the
//     same company see the rating read-only;
//   - only once Xobo approved the delivery: delivered, active and
//     verification_status 4, so not while pending approval, after docs
//     were rejected, or once the order was closed (5);
//   - editable for 7 days from the first rating (rating_editable_until),
//     then locked.
// Numeric coercion mirrors utils/orderStatus.ts, which reads statuses
// through Number() in case they arrive as strings.
// The list sends only the is_mine flag (never user_id), so there is no
// fallback: without the flag the form stays hidden.
const isMine = (row: any) => row.is_mine === true;
const isDeliveryApproved = (row: any) =>
  Number(row.order_status) === 4 && Number(row.status) === 1 && Number(row.verification_status) === 4;
const ratingLocked = (row: any) =>
  !!row.rating_editable_until && Date.now() >= Date.parse(row.rating_editable_until);
const canEditRating = (row: any) =>
  row.can_rate !== false && isMine(row) && isDeliveryApproved(row) && !ratingLocked(row);
const showSavedRating = (row: any) =>
  isDeliveryApproved(row) && (savedRating(row) !== null || !!row.driver_rating_comment);
// Only once rated: the window starts at the first Submit.
const ratingDeadline = (row: any) =>
  row.rating_editable_until ? DateTime.fromISO(row.rating_editable_until).toFormat("dd MMM yyyy, HH:mm") : "";
// The booker can land here with the window still open (a closed or
// not_approved refusal sets can_rate false), so "locked" only when it is.
const readOnlyRatingCaption = (row: any) => {
  if (!isMine(row)) return `Rated by ${row.user_name || "the person who booked this order"}. Only they can change it.`;
  return ratingLocked(row)
    ? "Ratings can be changed for 7 days after they are first submitted, so this one is now locked."
    : "This rating can no longer be changed.";
};
// Refusals after which the form must not stay open (orders-service codes).
const RATE_FINAL_REFUSALS = ["locked", "not_booker", "not_approved", "closed", "cancelled", "not_delivered", "no_driver"];
const draftOf = (row: any) => ratingDrafts.get(row.id)!;
const ratingShown = (row: any) => ratingHover.get(row.id) ?? draftOf(row).rating ?? 0;

// One tap-to-fill line per star level, worded to match how that rating
// feels, so a customer can leave a comment without typing.
const RATING_SUGGESTIONS: Record<number, string[]> = {
  1: ["Driver was very late", "Package arrived damaged", "Driver was unprofessional", "Could not reach the driver"],
  2: ["Delivery was late", "Package handled carelessly", "Poor communication", "Driver got lost"],
  3: ["Delivery was okay", "A little late", "Communication could be better", "Average experience"],
  4: ["Good, timely delivery", "Polite driver", "Package arrived safely", "Kept me updated"],
  5: ["Excellent, on time", "Very professional driver", "Handled with great care", "Great communication"],
};

// Hover previews the stars for a mouse only. A touch also fires
// pointerenter, and on a phone the preview then stuck after the tap,
// showing stars that were not the draft.
const previewRating = (row: any, n: number, e: PointerEvent) => {
  if (e.pointerType === "mouse") ratingHover.set(row.id, n);
};
// Tapping the selected star or chip again clears it, since both the
// rating and the comment are optional. Picking ends any preview, so the
// stars show the draft.
const pickRating = (row: any, n: number) => {
  const d = draftOf(row);
  d.rating = d.rating === n ? null : n;
  ratingHover.delete(row.id);
};
// Radio-group keys: arrows move the rating one star, Home/End jump to 1
// or 5, and focus follows the checked star. Space/Enter click as usual,
// so pressing the checked star clears it, like tapping it does.
const STAR_KEY_STEP: Record<string, number> = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 };
const onStarKey = (row: any, n: number, e: KeyboardEvent) => {
  let next = 0;
  if (e.key in STAR_KEY_STEP) next = Math.min(5, Math.max(1, n + STAR_KEY_STEP[e.key]));
  else if (e.key === "Home") next = 1;
  else if (e.key === "End") next = 5;
  if (!next) return;
  e.preventDefault();
  draftOf(row).rating = next;
  ratingHover.delete(row.id);
  const group = (e.currentTarget as HTMLElement).parentElement;
  group?.querySelectorAll<HTMLElement>("[role=radio]")[next - 1]?.focus();
};
// Describes the draft against what is saved, so the line under the stars
// never claims a rating that Submit is about to change or remove. Clearing
// the stars with an empty comment leaves Submit disabled (canSubmitRating
// needs one or the other), so that case says how removal actually works.
const ratingCaption = (row: any) => {
  const d = draftOf(row);
  const saved = savedRating(row);
  if (d.rating === saved) return saved ? `You rated ${saved} of 5` : "Tap a star to rate (optional)";
  if (d.rating !== null) return `Press Submit to save your ${d.rating}-star rating`;
  return d.comment.trim()
    ? `Your ${saved}-star rating will be removed when you press Submit`
    : `Your ${saved}-star rating stays saved. To remove it, add a comment and press Submit.`;
};
const pickSuggestion = (row: any, text: string) => {
  const d = draftOf(row);
  d.comment = d.comment === text ? "" : text;
};

// Needs something to send, and something that differs from what is
// already saved, so Submit never fires a no-op request.
const canSubmitRating = (row: any) => {
  const d = draftOf(row);
  const comment = d.comment.trim();
  if (ratingSaving.has(row.id) || (!d.rating && !comment)) return false;
  return d.rating !== savedRating(row) || comment !== (row.driver_rating_comment ?? "");
};

const submitRating = async (row: any) => {
  if (!canSubmitRating(row)) return;
  const d = draftOf(row);
  ratingSaving.add(row.id);
  try {
    // Always the full state (both fields) so the server never has to merge.
    // Token goes in the body as well as the default Bearer header.
    const r = await axios.post(`${import.meta.env.VITE_ORDERS_SERVICE}orders/rate`, {
      order_number: row.order_number,
      rating: d.rating,
      comment: d.comment.trim() || null,
      token: store.getToken,
    });
    if (!r.data?.status) throw new Error(r.data?.message);
    // The server sends back the saved state, including the edit deadline
    // the first Submit starts.
    const saved = r.data.data || {};
    row.driver_rating = saved.driver_rating ?? null;
    row.driver_rating_comment = saved.driver_rating_comment ?? null;
    if ("driver_rated_at" in saved) row.driver_rated_at = saved.driver_rated_at;
    if ("rating_editable_until" in saved) row.rating_editable_until = saved.rating_editable_until;
    if ("can_rate" in saved) row.can_rate = saved.can_rate;
    d.rating = savedRating(row);
    d.comment = row.driver_rating_comment ?? "";
    ElMessage({ message: "Thanks for your feedback", type: "success" });
  } catch (err: any) {
    // Draft is left as typed so the customer can simply retry, unless the
    // server says this order can no longer be rated by them (e.g. the 7
    // days ran out while the page was open): then the form gives way to
    // the read-only view.
    if (RATE_FINAL_REFUSALS.includes(err?.response?.data?.code)) row.can_rate = false;
    ElMessage({ message: err?.response?.data?.message || err?.message || "Could not save your feedback.", type: "error" });
  } finally {
    ratingSaving.delete(row.id);
  }
};

// Why this order cannot be rated by this viewer right now, in the order
// orders-service checks (rateDriver: not_booker first), or null when it
// can. no_driver has no list field, so can_rate false covers it.
const rateBlockReason = (row: any): string | null => {
  if (!isMine(row)) return "Only the person who booked this order can rate the driver.";
  if (Number(row.status) !== 1) return "This order was cancelled, so it can't be rated.";
  if (Number(row.order_status) !== 4) return "You can rate the driver once the order has been delivered.";
  if (Number(row.verification_status) === 5) return "This order was closed by Xobo Ops, so it can't be rated.";
  if (Number(row.verification_status) !== 4) return "You can rate the driver once Xobo has approved the delivery.";
  if (ratingLocked(row)) return "Ratings can be changed for 7 days after they are first submitted, so this one is now locked.";
  if (row.can_rate === false) return "This order can't be rated.";
  return null;
};

// Receipt-email deep link: /transactions/orders?rate=<ORDER_NUMBER>&stars=<1-5>.
// Handled once, after the first list load that succeeds, so a failed load
// does not use it up and a later refetch (filter change) never yanks the
// customer back to that order. rate/stars are then dropped from the URL,
// so a reload or a copied link does not run it again. The row opens the
// way a click does; the stars are pre-selected and Submit focused, but
// nothing is sent until the customer presses it. Each way it can fail
// says why: order not found, or not rateable by this viewer.
let rateLinkHandled = false;
const applyRateLink = async () => {
  if (rateLinkHandled) return;
  rateLinkHandled = true;
  const { rate, stars, ...rest } = router.currentRoute.value.query;
  if (!rate) return;
  router.replace({ query: rest });
  const orderNumber = String(rate).trim().toUpperCase();
  const idx = filteredData.value.findIndex((r: any) => String(r.order_number).toUpperCase() === orderNumber);
  if (idx < 0) {
    // Echo the value only when it looks like an order number, so a crafted
    // link cannot put arbitrary text in our UI.
    const label = /^[A-Z0-9-]{3,32}$/.test(orderNumber) ? `order ${orderNumber}` : "that order";
    ElMessage({ message: `We couldn't find ${label} in your order history.`, type: "warning", duration: 6000 });
    return;
  }
  const row = filteredData.value[idx];
  currentPage.value = Math.floor(idx / PAGE_SIZE) + 1;
  await openRow(row);
  const blocked = rateBlockReason(row);
  const n = Number(stars);
  if (blocked) {
    ElMessage({ message: `Order ${row.order_number}: ${blocked}`, type: "info", duration: 6000 });
  } else if (Number.isInteger(n) && n >= 1 && n <= 5) {
    // The caption then reads "Press Submit to save your N-star rating".
    draftOf(row).rating = n;
  }
  await nextTick();
  const wrap = document.getElementById(`oh-row-${row.id}`);
  const section = wrap?.querySelector<HTMLElement>(".oh-rating");
  (section || wrap)?.scrollIntoView({ behavior: "smooth", block: section ? "center" : "start" });
  if (!blocked && canSubmitRating(row)) {
    wrap?.querySelector<HTMLButtonElement>(".oh-rating-submit")?.focus({ preventScroll: true });
  }
};

// CSV only — hide the unassigned placeholder; UI/backend keep "Xobo Driver".
const PLACEHOLDER_DRIVER = "Xobo Driver";
const csvDriverName = (row: any) => {
  const name = row.orderDetails?.driver_details?.name?.trim() || "";
  if (!name || name === PLACEHOLDER_DRIVER) return "";
  return name;
};

// ─── Date range filter state ─────────────────────────────────────
// Bound to the header's el-date-picker (daterange). Element Plus
// emits [startISO, endISO] (both YYYY-MM-DD) or null when cleared.
// Passed through as start_date / end_date to the orders backend —
// those params are already accepted by services/orders/router.js and
// used in models/orderModel.js's DATE(scheduled_time) BETWEEN clause.
const dateRange = ref<[string, string] | null>(null)

// Server-side page cap. Orders backend defaults to 50 when limit isn't
// sent; explicit high limit here so customers with thousands of orders
// see the full set (the current view is client-paginated anyway, so
// pulling once and slicing is cheaper than N round-trips). 100k gives
// a very wide margin without slowing a typical company's fetch.
const FETCH_LIMIT = 100000

const buildOrdersUrl = (extra: Record<string, string | number | undefined> = {}) => {
  const base = `${import.meta.env.VITE_ORDERS_SERVICE}orders`
  const params: Record<string, string> = {
    company_id: String(store.getUserData.company_id),
    user_id:    String(store.getUserData.user_id),
    order_status: '*',
    limit:      String(FETCH_LIMIT),
  }
  if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
    params.start_date = dateRange.value[0]
    params.end_date   = dateRange.value[1]
  }
  // Cancelled orders are excluded server-side by default (status IN
  // (1,2,3,4)). Sending include_cancelled=1 widens the set to (0,1,2,3,4)
  // so the customer can see & search their cancelled orders too.
  if (includeCancelled.value) {
    params.include_cancelled = '1'
  }
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined && v !== null && v !== '') params[k] = String(v)
  }
  const qs = new URLSearchParams(params).toString()
  return `${base}?${qs}`
}

const postData = async () => {
  loading.value = true;
  let loaded = false;
  try {
    // The JWT goes in the header, never the URL (access logs, history).
    const r = await axios.get(buildOrdersUrl(), {
      headers: store.getToken ? { Authorization: `Bearer ${store.getToken}` } : {},
    });
    if (r.status == 200) {
      loaded = Array.isArray(r.data.data);
      // Replace, don't append — fetch is idempotent for the current
      // filter. Was pushing on every call which would double-render
      // on any subsequent postData().
      tableData.data.splice(0, tableData.data.length)
      if (Array.isArray(r.data.data)) r.data.data.forEach((el: any) => tableData.data.push(el));
      currentPage.value = 1
    }
    else ElMessage({ message: "Failed to load orders.", type: "error" });
  } catch { ElMessage({ message: "Failed to load order history.", type: "error" }); }
  loading.value = false;
  if (loaded) applyRateLink();
};
postData();

const onDateRangeChange = () => {
  // Re-fetch when the range changes (or clears). No debounce — the
  // date-picker only fires @change on picker close, not per-key.
  postData()
};

const onIncludeCancelledChange = () => {
  // Mirrors onDateRangeChange — the toggle is another server-side
  // filter, so flipping it always requires a fresh fetch. Search and
  // user-name filter are client-side and do NOT trigger a fetch.
  postData()
};

const transformDate = (date: string) => DateTime.fromISO(date).toFormat("dd MMM yyyy, HH:mm");
const transformDateSQL = (date: string) => DateTime.fromISO(date, { zone: "UTC" }).toFormat("dd MMM yyyy, HH:mm");

// Pill variant resolver — MUST check the row-level cancelled flag before the
// numeric order_status ladder. A cancelled order can carry order_status = 1
// (Pending) simultaneously: cancellation lives on customer_order.status = 0,
// not on the order_status column. If we checked order_status first, cancelled
// rows would fall into 'oh-badge--pending' (red) and the operator would
// treat them as still-live work.
// Compose a single OrderStatusView per row (kind + label + hint + tone)
// keyed on order_status + verification_status. Cached by row identity so
// the template's badge / label / hint / class getters resolve to the
// same object each render — cheap and avoids double-recomputing the
// same status.
const statusViewByRow = new WeakMap<object, ReturnType<typeof orderStatusView>>();
const statusFor = (row: any) => {
  if (!row || typeof row !== 'object') return orderStatusView(row || {});
  let v = statusViewByRow.get(row);
  if (!v) {
    v = orderStatusView(row);
    statusViewByRow.set(row, v);
  }
  return v;
};
const statusLabel = (row: any) => statusFor(row).label;
const getStatusClass = (row: any) => orderHistoryBadgeClass(statusFor(row));

// Internal nav link — opens the authenticated TrackOrder page. Prefer the
// unguessable tracking_token; fall back to order_number for legacy rows.
const returnOrderTrackingLink = (row: { tracking_token?: string | null; order_number: string }) =>
  row.tracking_token ? `/track?token=${row.tracking_token}` : `/track?order=${row.order_number}`;

// Public shareable link for the recipient — opens the unauthenticated /trip
// page with the stripped payload. Only meaningful when tracking_token exists.
const returnPublicTripLink = (row: { tracking_token?: string | null }) =>
  row.tracking_token
    ? `${window.location.origin}/trip?token=${row.tracking_token}`
    : '';
const hasDeliveryDocuments = (path: any) => path.some((obj: any) => obj.hasOwnProperty("document_info"));
const extractDocumentsInfo = (data: any): any[] => data.flatMap((item: any) => item.document_info ? item.document_info.map((doc: any) => ({ src: `${import.meta.env.VITE_SPACES_OBJECT_STORAGE}${doc.location}`, title: doc.document_type })) : []);

const isImage = (src: string) => /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(src);
const getExt = (src: string) => { const m = src?.match(/\.([a-z0-9]+)$/i); return m ? m[1].toUpperCase() : 'FILE'; };

// ── Doc preview ────────────────────────────────────────────────
// Single-doc preview state. The DocumentPreview modal handles one
// document at a time (image / pdf / office / text / unknown); the doc
// gallery beneath the expanded row acts as the "picker" — clicking a
// thumbnail sets modalDoc, closing the modal clears it.
//
// Shape matches DocumentPreview's PreviewSource interface: {file_url,
// doc_type}. We map from the local {src, title} gallery shape at the
// call site so the rest of the file doesn't have to change.
const modalDoc = ref<{ file_url: string; doc_type?: string } | null>(null);
const openDoc = (doc: { src: string; title?: string }) => {
  modalDoc.value = { file_url: doc.src, doc_type: doc.title };
};
const closeDoc = () => { modalDoc.value = null; };

// ── Rejection reason lookup ───────────────────────────────────
// Fetched once (module-level cache in ../../utils/rejectionReasons) and
// stored here as a reactive map so the template's rejectionInfo() helper
// can synchronously resolve a code → label. Kicks off on component setup
// so the reason is usually already resolved by the time the user opens
// a rejected row.
const rejectionReasonMap = ref<Record<string, RejectionReason>>({});
loadRejectionReasons().then((map) => { rejectionReasonMap.value = map; });

// True when the delivery-doc review flagged this order back to the
// customer — verification_status=2 (operator-rejected). Distinct from
// verification_status=5 which is a permanent close by Ops (customer
// was never charged) and reads as "Closed", NOT "Rejected".
const isRejectedVerification = (row: any): boolean => {
  const v = row?.verification_status ?? row?.orderDetails?.order_details?.verification_status;
  return v === 2;
};

// Returns {label, note} for the amber "Delivery documents rejected"
// expanded-row banner, or null when the order isn't in the rejected
// state. Reads from the loaded order_details (the list row may not
// carry the reason fields), so the banner only appears after expand —
// that's fine because the banner *is* inside the expanded area.
//
// PREVIOUSLY this returned truthy for verification_status=5 too,
// causing permanently-closed orders to render an amber "Delivery
// documents rejected" banner with the *closing* reason text — a
// direct contradiction with the violet "Closed" pill produced by
// orderStatusView (which correctly treats 5 as cancelled-tone). See
// closedInfo() below for the corresponding closed-order banner.
const rejectionInfo = (row: any): { label: string; note: string } | null => {
  const od = row?.orderDetails?.order_details;
  if (!od) return null;
  const v = od.verification_status;
  if (v !== 2) return null;
  return {
    label: labelForCode(rejectionReasonMap.value, od.rejection_reason_code) || "Documents rejected",
    note: (od.rejection_reason_note || "").trim(),
  };
};

// Returns {label, note} for the violet "Order closed" expanded-row
// banner, or null when the order isn't closed. verification_status=5
// means Ops permanently closed the order without ever charging the
// customer or crediting the driver; the reason fields carry the
// closing reason, not a rejection reason.
const closedInfo = (row: any): { label: string; note: string } | null => {
  const od = row?.orderDetails?.order_details;
  if (!od) return null;
  if (od.verification_status !== 5) return null;
  return {
    label: labelForCode(rejectionReasonMap.value, od.rejection_reason_code) || "Order closed by Xobo Ops",
    note: (od.rejection_reason_note || "").trim(),
  };
};

// Drop-offs are every location after the pickup (index 0). Joined by " -- ";
// the separator only appears when there is more than one drop. Falls back to
// the single last-stop name if the locations array is unavailable.
const formatDropLocations = (row: any): string => {
  if (Array.isArray(row.locations) && row.locations.length > 1) {
    return row.locations.slice(1).map((l: any) => l?.name ?? "").filter(Boolean).join(" -- ");
  }
  return row.dropoff || "";
};

const csvDownloading = ref(false)

// Full-set CSV — exports the currently-filtered on-screen rows. Because
// the initial fetch uses FETCH_LIMIT=100000 the "loaded set" is already
// the full history for the current server-side filter (date range +
// include_cancelled), so no separate refetch is needed. The per-row
// detail payload (email/phone/driver/vehicle/notes/manifest) is
// hydrated lazily on expand; here we batch-hydrate any rows that haven't
// been expanded yet so every exported row carries the full column set.
const downloadCSV = async () => {
  const exportRows = filteredData.value;
  if (!exportRows.length) {
    ElMessage({ message: 'No data to download for the selected filters.', type: 'warning' });
    return;
  }
  csvDownloading.value = true;
  try {
    const pending = exportRows.filter((r: any) => !r.orderDetails);
    const BATCH = 8;
    for (let i = 0; i < pending.length; i += BATCH) {
      await Promise.all(pending.slice(i, i + BATCH).map((r: any) => fetchOrderDetails(r).catch(() => undefined)));
    }

    // csvSafeRow: free text that starts like a formula is exported as text.
    const csv = unparse(exportRows.map((row: any) => csvSafeRow({
      "Date": transformDateSQL(row.scheduled_time),
      "Order ID": row.order_number,
      "User Name": row.user_name,
      "User Email": row.orderDetails?.customer_details?.user_email || "",
      "User Phone": row.orderDetails?.customer_details?.phone_number || "",
      "Driver name": csvDriverName(row),
      "Vehicle Type": resolveVehicleTypeName(row),
      "Order Status": statusLabel(row),
      "Distance (Km)": row.distance_value,
      "Pickup Location": row.pickup,
      "Drop Location(s)": formatDropLocations(row),
      "Notes": row.notes || "",
      "Manifest": row.manifest_number || "",
      "Ride Amount": row.cost,
      "Type": orderTypeLabel(extraOf(row, "order_type")),
      "Pickup mode": pickupModeLabel(extraOf(row, "pickup_mode")),
      "Goods": String(extraOf(row, "goods_description") ?? "").trim(),
      "Loaders": Number(extraOf(row, "loaders_count")) || 0,
      // Drop-offs only, in stop order; the list's stop_references when the
      // detail has not loaded.
      "Drop references": dropReferences(row.orderDetails?.stop_details, row.stop_references),
    })));
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    // Include the filter in the filename so multiple exports don't collide.
    const rangeTag = dateRange.value ? `${dateRange.value[0]}_to_${dateRange.value[1]}` : 'all'
    const stamp = new Date().toISOString().slice(0, 10)
    link.download = `orders-${rangeTag}-${stamp}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  } catch (err: any) {
    ElMessage({ message: err?.message || 'Failed to export CSV', type: 'error' });
  } finally {
    csvDownloading.value = false;
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');
* { box-sizing: border-box; }

/* Page chrome (header, toolbar, controls, buttons) lives in
   styles/list-page.scss — shared with Users / Payments / Statement.
   Only the filter widths specific to this page are set here. */
.oh-date-range { flex: 0 1 250px; }
.oh-user-select { flex: 0 1 180px; }

/* ── List wrapper ──────────────────────── */
.oh-list-wrap { padding-top: 4px; padding-bottom: 48px; }

/* ── Table ─────────────────────────────── */
.oh-table {
  width: 100%;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

/* Header row */
.oh-thead {
  display: grid;
  grid-template-columns: 140px 120px 1fr 150px 120px 1fr 1fr 90px;
  background: #f8fafc;
  border-bottom: 1.5px solid #e2e8f0;
  padding: 0 20px;
}
.oh-th {
  font-size: 0.68rem; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.08em;
  padding: 12px 10px; white-space: nowrap;
}
.oh-th--amount { text-align: right; }
.oh-th--action { text-align: right; }

/* Body */
.oh-tbody { display: flex; flex-direction: column; }

/* Row wrap */
.oh-row-wrap { border-bottom: 1px solid #f1f5f9; }
.oh-row-wrap:last-child { border-bottom: none; }
.oh-row-wrap--open { background: white; }

/* Data row */
.oh-row {
  display: grid;
  grid-template-columns: 140px 120px 1fr 150px 120px 1fr 1fr 90px;
  align-items: center;
  padding: 0 20px;
  height: 56px;
  cursor: pointer;
  transition: background 0.12s;
}
.oh-row:hover { background: #f8fafc; }
.oh-row-wrap--open .oh-row { background: #f8fafc; }

/* Cells */
.oh-td { padding: 0 10px; overflow: hidden; }

.oh-ref {
  font-size: 0.84rem; font-weight: 700; color: #12284A;
  letter-spacing: 0.04em; text-transform: uppercase; white-space: nowrap;
}

.oh-td-text {
  font-size: 0.83rem; color: #334155; font-weight: 500;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block;
  text-transform: capitalize;
}

.oh-td-muted {
  font-size: 0.81rem; color: #64748b; font-weight: 400;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block;
}

.oh-td-amount {
  font-size: 0.84rem; color: #12284A; font-weight: 700;
  text-align: right; display: block; white-space: nowrap;
}

/* Status badge — pill outline style matching the reference */
.oh-badge {
  display: inline-flex; align-items: center;
  padding: 4px 12px; border-radius: 20px;
  font-size: 0.74rem; font-weight: 600; white-space: nowrap;
  border: 1.5px solid currentColor;
}
.oh-badge--pending   { color: #dc2626; background: #fef2f2; border-color: #fca5a5; }
.oh-badge--active    { color: #2563eb; background: #eff6ff; border-color: #93c5fd; }
.oh-badge--delivered { color: #16a34a; background: #f0fdf4; border-color: #86efac; }
/* Cancelled — violet, so it reads distinctly from the red Pending pill and
   the grey default. Same palette family as the Route-card icon above. */
.oh-badge--cancelled { color: #6d28d9; background: #f5f3ff; border-color: #c4b5fd; }
.oh-badge--default   { color: #64748b; background: #f8fafc; border-color: #cbd5e1; }
/* Warning — amber. Used for "Pending approval" while delivery docs
   are being reviewed. Distinct from the red "Pending" (waiting for a
   driver) so customers can tell "no driver yet" from "delivered,
   awaiting review". */
.oh-badge--warning   { color: #b45309; background: #fef3c7; border-color: #fcd34d; }
/* Danger — matches the standalone .oh-badge--rejected chip's palette,
   used when verification_status = 2 (Docs rejected). */
.oh-badge--danger    { color: #b45309; background: #fef3c7; border-color: #fcd34d; }
/* Rejected — smaller amber chip that sits next to the status pill when
   the delivery-doc review has flagged the order. Amber (not red) so it
   doesn't get confused with the red "Pending" order-status pill; the
   two chips can sit side-by-side without shouting the same colour. */
.oh-badge--rejected {
  color: #b45309; background: #fef3c7; border-color: #fcd34d;
  margin-left: 6px; padding: 2px 8px; font-size: 0.68rem;
  letter-spacing: 0.02em; text-transform: uppercase;
}

/* Rejection banner — full-width strip at the top of the expanded row
   when verification_status is 2 or 5. Amber tone matches the row-level
   .oh-badge--rejected chip so the two indicators read as one thing. */
.oh-reject-banner {
  background: #fffbeb;
  border: 1.5px solid #fcd34d;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
  color: #78350f;
}
.oh-reject-banner__head {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.oh-reject-banner__head > svg { color: #b45309; flex-shrink: 0; }
.oh-reject-banner__title {
  font-size: 0.82rem; font-weight: 700; color: #92400e;
}
.oh-reject-banner__label {
  font-size: 0.75rem; font-weight: 600; color: #b45309;
  background: #fef3c7; border: 1px solid #fcd34d; border-radius: 999px;
  padding: 2px 10px; text-transform: capitalize;
}
.oh-reject-banner__note {
  margin: 6px 0 0; font-size: 0.8rem; line-height: 1.4; color: #78350f;
  white-space: pre-wrap; word-break: break-word;
}

/* Ops-closed banner (verification_status=5). Violet tone matches the
   .oh-badge--cancelled chip painted by orderStatusView so the row
   badge and the expanded-detail banner read as one thing — distinct
   from the amber rejection banner above. */
.oh-close-banner {
  background: #f5f3ff;
  border: 1.5px solid #c4b5fd;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
  color: #4c1d95;
}
.oh-close-banner__head {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.oh-close-banner__head > svg { color: #7c3aed; flex-shrink: 0; }
.oh-close-banner__title {
  font-size: 0.82rem; font-weight: 700; color: #5b21b6;
}
.oh-close-banner__label {
  font-size: 0.75rem; font-weight: 600; color: #6d28d9;
  background: #ede9fe; border: 1px solid #c4b5fd; border-radius: 999px;
  padding: 2px 10px; text-transform: capitalize;
}
.oh-close-banner__note {
  margin: 6px 0 0; font-size: 0.8rem; line-height: 1.4; color: #4c1d95;
  white-space: pre-wrap; word-break: break-word;
}
.oh-close-banner__hint {
  margin: 6px 0 0; font-size: 0.75rem; line-height: 1.4; color: #6d28d9;
  font-style: italic;
}

/* View link */
.oh-view-link {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.8rem; font-weight: 600; color: #2563eb;
  float: right; white-space: nowrap; transition: gap 0.15s;
}
.oh-row:hover .oh-view-link { gap: 7px; }

/* ── Expanded detail ────────────────────── */
.oh-detail {
  border-top: 1.5px solid #e2e8f0;
  background: #f0f4fa;
  padding: 14px;
}
.oh-detail-grid { display: grid; grid-template-columns: 1fr 1.3fr 0.85fr; gap: 10px; }
/* The Driver card holds the rating: five 40px stars plus gaps need about
   210px inside the card, which the third column only has above ~1100px.
   Below that the Driver card takes a full row under the other two, and on
   phones every card stacks. Without this the stars and Submit were cut off
   by the card's overflow: hidden (74px-wide card at 390px). */
@media (max-width: 1100px) {
  .oh-detail-grid { grid-template-columns: 1fr 1.3fr; }
  .oh-dcard--driver { grid-column: 1 / -1; }
}
@media (max-width: 720px) {
  .oh-detail-grid { grid-template-columns: 1fr; }
}

.oh-dcard {
  background: white; border-radius: 10px;
  border: 1.5px solid #e2e8f0; padding: 14px 16px;
  min-width: 0;
  overflow: hidden;
}
.oh-dcard-head { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; }
.oh-dcard-icon {
  width: 28px; height: 28px; border-radius: 7px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.oh-dcard-icon--blue   { background: #eff6ff; }
.oh-dcard-icon--purple { background: #f5f3ff; }
.oh-dcard-icon--teal   { background: #f0fdfa; }
.oh-dcard-label { font-size: 0.72rem; font-weight: 700; color: #334155; text-transform: uppercase; letter-spacing: 0.07em; }

.oh-dkv-list { display: flex; flex-direction: column; gap: 0; }
.oh-dkv {
  display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;
  padding: 8px 0; border-bottom: 1px solid #f1f5f9;
}
.oh-dkv:last-child { border-bottom: none; padding-bottom: 0; }
.oh-dk { font-size: 0.78rem; color: #64748b; font-weight: 500; flex-shrink: 0; max-width: 42%; }
.oh-dv {
  flex: 1;
  min-width: 0;
  font-size: 0.83rem; color: #0f172a; font-weight: 600; text-align: right;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.oh-dv--wrap { white-space: normal; }
.oh-dv--navy { color: #12284A; font-weight: 700; }
.oh-dv--green { color: #15803d; font-weight: 600; }

/* Route */
.oh-stop { display: flex; gap: 10px; }
.oh-stop-track { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; padding-top: 4px; }
.oh-stop-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.oh-stop-dot--green { background: #22c55e; box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
.oh-stop-dot--amber { background: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.15); }
.oh-stop-dot--mid { background: #94a3b8; }
.oh-stop-line { width: 2px; flex: 1; min-height: 14px; background: #e2e8f0; margin: 3px 0; }
.oh-stop-text { flex: 1; padding-bottom: 10px; }
.oh-stop-type {
  font-size: 0.67rem; color: #94a3b8; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  display: flex; align-items: center; gap: 5px;
}
.oh-stop-type--drop { color: #64748b; }
.oh-stop-num {
  display: inline-flex; align-items: center; justify-content: center;
  width: 15px; height: 15px; border-radius: 50%;
  background: #f1f5f9; color: #475569;
  font-size: 0.62rem; font-weight: 700; flex-shrink: 0;
}
.oh-stop-name { font-size: 0.81rem; color: #1e293b; font-weight: 500; margin-top: 3px; line-height: 1.35; }
/* Receiver contact under a stop: "Jane Wanjiru · 0722 000 111 · Ref INV-77". */
.oh-stop-contact { font-size: 0.76rem; color: #475569; margin-top: 3px; line-height: 1.4; overflow-wrap: anywhere; }
.oh-stop-contact > span + span::before { content: " · "; color: #94a3b8; }
.oh-stop-tel { color: #12284A; font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }
.oh-stop-instructions { font-size: 0.75rem; color: #64748b; font-style: italic; margin-top: 2px; line-height: 1.4; overflow-wrap: anywhere; }
.oh-detail-actions { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
.oh-track-link { font-size: 0.81rem; font-weight: 600; color: #2563eb; text-decoration: none; }
.oh-track-link:hover { color: #1d4ed8; }
.oh-docs-section, .oh-rating { margin-top: 12px; border-top: 1px solid #f1f5f9; padding-top: 10px; }

.oh-docs-toggle {
  display: flex; align-items: center; gap: 6px;
  background: none; border: none; padding: 0; cursor: pointer;
  font-family: 'DM Sans', sans-serif; font-size: 0.8rem; font-weight: 600;
  color: #475569; transition: color 0.15s; width: 100%;
}
.oh-docs-toggle:hover { color: #12284A; }
.oh-docs-count {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 50%;
  background: #e2e8f0; color: #475569;
  font-size: 0.65rem; font-weight: 700;
}
.oh-docs-chevron { margin-left: auto; color: #94a3b8; transition: transform 0.2s; }
.oh-docs-chevron--open { transform: rotate(180deg); }

.oh-docs-gallery {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 8px; margin-top: 10px;
}
.oh-doc-thumb {
  text-decoration: none; display: flex; flex-direction: column; gap: 5px;
  cursor: pointer;
}
.oh-doc-img-wrap {
  position: relative; border-radius: 8px; overflow: hidden;
  aspect-ratio: 1; background: #f1f5f9;
  border: 1.5px solid #e2e8f0; transition: border-color 0.15s;
}
.oh-doc-thumb:hover .oh-doc-img-wrap { border-color: #2563eb; }
.oh-doc-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.oh-doc-overlay {
  position: absolute; inset: 0; background: rgba(18,40,74,0.55);
  display: flex; align-items: center; justify-content: center;
  opacity: 0; transition: opacity 0.18s;
}
.oh-doc-thumb:hover .oh-doc-overlay { opacity: 1; }
.oh-doc-label {
  font-size: 0.68rem; color: #64748b; font-weight: 500;
  text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* docs expand transition */
.docs-expand-enter-active, .docs-expand-leave-active { transition: all 0.22s cubic-bezier(0.4,0,0.2,1); overflow: hidden; }
.docs-expand-enter-from, .docs-expand-leave-to { opacity: 0; max-height: 0; }
.docs-expand-enter-to, .docs-expand-leave-from { opacity: 1; max-height: 400px; }

/* Driver */
.oh-driver { display: flex; align-items: center; gap: 12px; }
.oh-driver-avatar {
  width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%);
  color: white; display: flex; align-items: center; justify-content: center;
  font-size: 1rem; font-weight: 700;
}
.oh-driver-name { font-size: 0.86rem; font-weight: 600; color: #12284A; }
.oh-driver-plate {
  display: inline-block; background: #f1f5f9; border-radius: 4px;
  padding: 2px 8px; font-size: 0.71rem; font-weight: 600;
  color: #475569; letter-spacing: 0.06em; margin-top: 4px;
}

/* Driver rating. The divider above it shares the .oh-docs-section rule
   so both card separators stay one style. Stars are outline grey until
   hovered or saved, then amber (same amber as the drop-off dot). */
.oh-stars { display: flex; gap: 2px; margin-top: 6px; }
/* Pull the row back by the hit area's inset so the first star lines up
   with the label above it; the card's 16px padding absorbs it. */
.oh-rating .oh-stars:not(.oh-stars--static) { margin-left: -10px; }
.oh-star {
  display: inline-flex; align-items: center; justify-content: center;
  /* 40px hit area around the 20px icon, so a thumb lands on one star. */
  min-width: 40px; min-height: 40px; background: none; border: none; padding: 0;
  /* Empty-star outline at 3.5:1 on white (WCAG 1.4.11 needs 3:1; the old
     #cbd5e1 was 1.5:1, so unselected stars barely showed). */
  border-radius: 6px; cursor: pointer; color: #7c8aa0; transition: color 0.12s;
}
/* Selected: amber fill inside a darker amber outline at 3.2:1, since the
   amber fill alone is only 2.2:1 on white. */
.oh-star--on { color: #d97706; }
.oh-star--on svg { fill: #f59e0b; }
.oh-star:focus-visible { outline: 2px solid #12284A; outline-offset: 1px; }
.oh-star:disabled { cursor: progress; opacity: 0.6; }
.oh-rating-caption { font-size: 0.75rem; color: #64748b; margin-top: 4px; }
/* The section label reuses .oh-stop-type, whose #94a3b8 is 2.6:1 on white,
   below the 4.5:1 small text needs. Darkened here only. */
.oh-rating .oh-stop-type { color: #475569; }
/* Read-only rating: the same stars as plain icons, the comment as text. */
.oh-stars--static { gap: 4px; }
.oh-star-static { color: #7c8aa0; flex-shrink: 0; }
.oh-star-static.oh-star--on { color: #d97706; fill: #f59e0b; }
.oh-rating-saved-comment {
  margin: 6px 0 0; font-size: 0.8rem; line-height: 1.4; color: #1e293b;
  white-space: pre-wrap; overflow-wrap: anywhere;
}
/* Suggested comments: pill shape borrowed from .oh-badge, navy when the
   comment box holds exactly that text. */
.oh-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.oh-chip {
  padding: 4px 10px; border-radius: 20px; cursor: pointer;
  border: 1.5px solid #e2e8f0; background: white; color: #475569;
  font-family: 'DM Sans', sans-serif; font-size: 0.74rem; font-weight: 600;
  transition: all 0.15s;
}
/* Hover only where it exists: on touch, :hover sticks after a tap. */
@media (hover: hover) {
  .oh-chip:hover { border-color: #12284A; }
}
.oh-chip--on { background: #12284A; border-color: #12284A; color: white; }
.oh-chip:focus-visible { outline: 2px solid #12284A; outline-offset: 1px; }
/* While a rating is saving, the draft is frozen so what is sent is what
   is shown. */
.oh-chip:disabled, .oh-rating-comment:disabled { cursor: progress; opacity: 0.6; }
.oh-chip:disabled:hover { border-color: #e2e8f0; }
.oh-rating-comment {
  display: block; width: 100%; box-sizing: border-box; margin-top: 8px;
  padding: 8px 10px; border: 1.5px solid #e2e8f0; border-radius: 8px;
  font-family: 'DM Sans', sans-serif; font-size: 0.8rem; color: #1e293b;
  resize: vertical; outline: none; transition: border-color 0.15s;
}
.oh-rating-comment:focus { border-color: #12284A; }
/* Compact variant of the shared .xo-btn so it sits quietly in the card. */
.oh-rating-submit { height: 32px; padding: 0 14px; margin-top: 8px; font-size: 0.78rem; }
/* Touch and phone widths: 16px text so iOS Safari does not zoom the page
   when the comment box is focused, and a thumb-sized Submit. */
@media (max-width: 720px), (pointer: coarse) {
  .oh-rating-comment { font-size: 16px; }
  .oh-rating-submit { height: 40px; padding: 0 18px; font-size: 0.85rem; }
}

/* Transition */
.expand-enter-active, .expand-leave-active { transition: all 0.22s cubic-bezier(0.4,0,0.2,1); overflow: hidden; }
.expand-enter-from, .expand-leave-to { opacity: 0; max-height: 0; }
.expand-enter-to, .expand-leave-from { opacity: 1; max-height: 500px; }

/* Empty */
.oh-empty { text-align: center; padding: 60px 0; display: flex; flex-direction: column; align-items: center; gap: 12px; }
.oh-empty-icon { font-size: 2.5rem; }
.oh-empty-text { font-size: 1rem; font-weight: 500; color: #94a3b8; }

/* Document preview modal chrome lives inside the shared DocumentPreview
   component (src/components/DocumentPreview.vue). The bespoke .dm-* /
   .modal-fade-* rules that previously lived here are gone with the
   modal they styled. */

</style>
